/**
 * 认知管理 · 知识图谱 —— 单体/群体两个角度。
 *
 * 数据成色(台面上必须看得见的三件事):
 *  1. 这里只有【PIPL 过滤后】的知识:地点/物体/事实。人物、对话、照片永远不出
 *     设备 —— 狗侧 ksync 按人名三层过滤,hub 白名单兜底,过滤计数(dropped)
 *     随快照一起上报,就显示在本页。
 *  2. 群体视图【不合并】同名实体:实体统一 ID 还没建,同名只列"疑似候选",
 *     谁也不许把两台设备看到的同一张桌子算成一个。
 *  3. 设备自报的时间(firstSeen/lastSeen)可能来自没有 RTC 的时钟(1970)——
 *     那种值显示"未知",不装作有日期。
 *  4. 图上一次只画【一张地图】。2026-08-10 用户问「记忆里是不是重复很多」:
 *     页面画了 329 个物体,而那正好是 185(08-09 那张图)+ 144(08-10 那张)——
 *     同一个办公室两天两张图,叠在同一张力导向图上。记忆没记重,是这里把两个
 *     坐标系当成了一个,连物体↔地点那条边的距离也就跟着没了意义。
 *     合并要等跨图对齐(重定位那条线);在那之前一次画一张,并把没画的说出来。
 *     判据 lib/__tests__/knowledgeMaps.test.mjs。
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useKnowledge, useTerminals } from '@/hooks/useHubData';
import { NoDataSource, LoadError } from '@/components/feature/DataSource';
import KnowledgeGraph from './components/KnowledgeGraph';
import { mapScopes, currentMapOf, scopeRows, type MapScope } from '@/lib/knowledgeMaps';
import type { HubKnowledgeGraph, HubKnowledgeSummary } from '@/lib/hubClient';

/* 设备着色调色板 —— 群体视图按归属设备着色。索引取设备在列表里的位置。 */
const DEVICE_PALETTE = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#06b6d4', '#f97316', '#8b5cf6', '#84cc16'];

/* 设备自报时间 < 2000 年 = 时钟没校准(狗无 RTC),显示"未知"而不是 1970。 */
const deviceTime = (ts: number): string =>
  ts >= 946684800
    ? new Date(ts * 1000).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '未知(时钟未校准)';

const syncTime = (ts: number): string =>
  ts > 0 ? new Date(ts * 1000).toLocaleString('zh-CN') : '—';

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <i className="ri-loader-4-line text-2xl text-foreground-400 animate-spin" />
    </div>
  );
}

/* ─── 一次只画一张图(见文件头第 4 条)───────────────────────────────────── */

/** 把快照按 (设备 → 图) 收窄。keep 为空 = 不收窄(还没算出来时的安全默认)。 */
function scopeGraph(graph: HubKnowledgeGraph, keep: Record<string, string>) {
  if (Object.keys(keep).length === 0) return { graph, droppedObjects: 0, droppedPlaces: 0 };
  const o = scopeRows(graph.objects, keep);
  const p = scopeRows(graph.places, keep);
  return {
    graph: { ...graph, objects: o.kept, places: p.kept },
    droppedObjects: o.dropped,
    droppedPlaces: p.dropped,
  };
}

/** 没画出来的那部分必须说出来 —— 少画了不说, 读图的人会把"我没画"读成"它没有"。 */
function ScopeNote({ scopes, keep, droppedObjects, droppedPlaces, onPick }: {
  scopes: MapScope[];
  keep: Record<string, string>;
  droppedObjects: number;
  droppedPlaces: number;
  onPick: (deviceId: string, mapId: string) => void;
}) {
  if (scopes.length <= 1) return null;      // 只有一张图 —— 没有可说的, 也没有可选的
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5 bg-amber-50/60 border border-amber-200/70 rounded-lg text-[11px] text-foreground-600">
      <span className="text-amber-700">
        <i className="ri-stack-line mr-1" />
        这台设备建过 {scopes.length} 张地图。图上只画当前这张 ——
        另外 {droppedObjects} 个物体 / {droppedPlaces} 个地点在别的坐标系里,不是重复记录。
      </span>
      <span className="flex flex-wrap items-center gap-1.5">
        {scopes.map((s) => {
          const on = keep[s.deviceId] === s.mapId;
          return (
            <button
              key={`${s.deviceId}-${s.mapId}`}
              onClick={() => onPick(s.deviceId, s.mapId)}
              title={`${s.mapId} · ${s.nObjects} 物体 / ${s.nPlaces} 地点 · 最近 ${
                s.lastSeen ? deviceTime(s.lastSeen) : '未知(时钟未校准)'}`}
              className={`px-2 py-0.5 rounded font-mono text-[10px] border transition-colors ${
                on ? 'bg-amber-500/15 border-amber-400 text-amber-800'
                   : 'bg-background-0 border-background-200 text-foreground-500 hover:border-amber-300'}`}
            >
              {s.mapId.replace(/^map_/, '')} · {s.nObjects}
            </button>
          );
        })}
      </span>
    </div>
  );
}

/* ─── hub 数据 → KnowledgeGraph 的 props(单体/群体共用)────────────────── */
function toGraphProps(graph: HubKnowledgeGraph, nameOf: (deviceId: string) => string) {
  const devices = graph.devices.map((d, i) => ({
    id: d.id,
    name: nameOf(d.id),
    color: DEVICE_PALETTE[i % DEVICE_PALETTE.length],
  }));
  const deviceColors: Record<string, string> = {};
  devices.forEach((d) => { deviceColors[d.id] = d.color; });

  return {
    terminals: devices.map((d) => ({ id: d.id, name: d.name, type: 'dog' })),
    deviceColors,
    places: graph.places.map((p) => ({
      id: `place-${p.deviceId}-${p.mapId}-${p.name}`,
      name: p.name,
      room: p.mapId,
      placeType: '',
      deviceId: p.deviceId,
      // 坐标带下去:物体↔地点那条边上的距离要【算】出来, 而不是像旧版那样哈希一个。
      x: p.x, y: p.y,
    })),
    objects: graph.objects.map((o) => ({
      id: `obj-${o.deviceId}-${o.instId}`,
      name: o.name,
      detectedBy: nameOf(o.deviceId),
      confidence: 1,            // nObs 在场时不使用,仅作兜底
      nObs: o.nObs,
      location: o.nearestPlace ?? '',
      deviceId: o.deviceId,
      x: o.x, y: o.y,
    })),
    facts: graph.facts.map((f, i) => ({
      id: `fact-${f.deviceId}-${i}`,
      content: `${f.subject} · ${f.predicate} → ${f.object || '?'}`,
      source: nameOf(f.deviceId),
      deviceId: f.deviceId,
      confidence: f.confidence,
    })),
  };
}

/* ─── 单体:一台设备的知识 ─────────────────────────────────────────────── */
function UnitView({ deviceId, graph, summary }: { deviceId: string; graph: HubKnowledgeGraph; summary: HubKnowledgeSummary }) {
  const nameOf = (id: string) => summary.devices.find((d) => d.deviceId === id)?.name || id;
  const meta = summary.devices.find((d) => d.deviceId === deviceId);
  const scopes = useMemo(() => mapScopes(graph.objects, graph.places), [graph]);
  // 用户选的图优先; 没选过就用"最近有动静的那张"。切设备/换快照时回到默认。
  const [picked, setPicked] = useState<Record<string, string>>({});
  useEffect(() => { setPicked({}); }, [deviceId]);
  const keep = useMemo(() => ({ ...currentMapOf(scopes), ...picked }), [scopes, picked]);
  const scoped = useMemo(() => scopeGraph(graph, keep), [graph, keep]);
  const props = toGraphProps(scoped.graph, nameOf);

  return (
    <div className="space-y-4">
      {meta && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-[11px] text-foreground-500">
          <span><i className="ri-time-line mr-1" />最近上报:{syncTime(meta.syncedAt)}(hub 打点)</span>
          <span>地点 {meta.nPlaces} · 物体 {meta.nObjects} · 事实 {meta.nFacts}</span>
          {meta.dropped && (meta.dropped.places + meta.dropped.attrs + meta.dropped.facts) > 0 && (
            <span className="text-emerald-600" title="PIPL 过滤在设备侧拦下的行数 —— 它们从未离开那台设备">
              <i className="ri-shield-check-line mr-1" />
              已拦截涉人行:{meta.dropped.places} 地点 / {meta.dropped.attrs} 属性 / {meta.dropped.facts} 事实
            </span>
          )}
        </div>
      )}

      <ScopeNote
        scopes={scopes}
        keep={keep}
        droppedObjects={scoped.droppedObjects}
        droppedPlaces={scoped.droppedPlaces}
        onPick={(d, m) => setPicked((prev) => ({ ...prev, [d]: m }))}
      />

      <div className="bg-background-50 border border-background-200/70 rounded-lg">
        <KnowledgeGraph
          objects={props.objects}
          terminals={props.terminals}
          facts={props.facts}
          places={props.places}
          colorMode="type"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="物体实例" icon="ri-box-3-line" count={scoped.graph.objects.length}>
          {scoped.graph.objects.length === 0 ? <Empty hint="这台设备还没记下任何物体" /> : (
            <table className="w-full text-[11px]">
              <thead><tr className="text-foreground-400 text-left">
                <th className="pb-1.5 font-medium">名称</th><th className="pb-1.5 font-medium">观测</th>
                <th className="pb-1.5 font-medium">位置参考</th><th className="pb-1.5 font-medium">最近见到</th>
              </tr></thead>
              <tbody>
                {scoped.graph.objects.map((o) => (
                  <tr key={`${o.deviceId}-${o.instId}`} className="border-t border-background-100">
                    <td className="py-1.5 text-foreground-800">{o.name}</td>
                    <td className="py-1.5 font-mono">{o.nObs}次</td>
                    <td className="py-1.5 text-foreground-500">{o.nearestPlace || o.mapId}</td>
                    <td className="py-1.5 text-foreground-400">{deviceTime(o.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="地点" icon="ri-map-pin-line" count={scoped.graph.places.length}>
          {scoped.graph.places.length === 0 ? <Empty hint="这台设备还没记下任何地点" /> : (
            <table className="w-full text-[11px]">
              <thead><tr className="text-foreground-400 text-left">
                <th className="pb-1.5 font-medium">名称</th><th className="pb-1.5 font-medium">地图</th><th className="pb-1.5 font-medium">说明</th>
              </tr></thead>
              <tbody>
                {scoped.graph.places.map((p) => (
                  <tr key={`${p.deviceId}-${p.mapId}-${p.name}`} className="border-t border-background-100">
                    <td className="py-1.5 text-foreground-800">{p.name}</td>
                    <td className="py-1.5 font-mono text-foreground-400">{p.mapId || '—'}</td>
                    <td className="py-1.5 text-foreground-500 max-w-[140px] truncate" title={p.descr}>{p.descr || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="事实" icon="ri-links-line" count={graph.facts.length}>
          {graph.facts.length === 0 ? <Empty hint="还没有提炼出事实(事实来自对话蒸馏)" /> : (
            <ul className="space-y-1.5">
              {graph.facts.map((f, i) => (
                <li key={i} className="text-[11px] leading-relaxed">
                  <span className="text-foreground-800">{f.subject}</span>
                  <span className="text-foreground-400 mx-1">—{f.predicate}→</span>
                  <span className="text-primary-600">{f.object || '?'}</span>
                  {f.text && <p className="text-foreground-400 mt-0.5" title={f.text}>{f.text.length > 40 ? f.text.slice(0, 40) + '…' : f.text}</p>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ─── 群体:全设备并集(不合并)────────────────────────────────────────── */
function GroupView({ graph, summary }: { graph: HubKnowledgeGraph; summary: HubKnowledgeSummary }) {
  const nameOf = (id: string) => summary.devices.find((d) => d.deviceId === id)?.name || id;
  // 群体视图同样一台设备只画一张图 —— 跨图叠画在这里更糟: 几台狗的几张图一起堆,
  // 而它们两两之间都没有对齐过。★ 是【每台各留一张】, 不是全局留一张:
  // 全局留一张会让其余设备整个消失, 那不是少画, 是"这台狗没有知识"(判据 ② 钉着)。
  const scopes = useMemo(() => mapScopes(graph.objects, graph.places), [graph]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const keep = useMemo(() => ({ ...currentMapOf(scopes), ...picked }), [scopes, picked]);
  const scoped = useMemo(() => scopeGraph(graph, keep), [graph, keep]);
  const props = toGraphProps(scoped.graph, nameOf);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon="ri-robot-line" label="已上报设备" value={`${summary.reportedDevices} / ${summary.reportedDevices + summary.neverPushed.length}`}
          note={`分母 = 注册未吊销设备;${summary.neverPushed.length} 台待首次上报`} />
        <Stat icon="ri-map-pin-line" label="地点合计" value={String(summary.totals.places)} note="含跨设备重复,未合并" />
        <Stat icon="ri-box-3-line" label="物体合计" value={String(summary.totals.objects)} note="含跨设备重复,未合并" />
        <Stat icon="ri-links-line" label="事实合计" value={String(summary.totals.facts)} note="PIPL 过滤后" />
      </div>

      <ScopeNote
        scopes={scopes}
        keep={keep}
        droppedObjects={scoped.droppedObjects}
        droppedPlaces={scoped.droppedPlaces}
        onPick={(d, m) => setPicked((prev) => ({ ...prev, [d]: m }))}
      />

      <div className="bg-background-50 border border-background-200/70 rounded-lg">
        {graph.devices.length === 0 ? (
          <NoDataSource title="还没有设备上报过知识" reason="设备端 ksync 每 5 分钟推送一次过滤后的知识快照;第一份到达后这里出现全舰队图谱。" />
        ) : (
          <KnowledgeGraph
            objects={props.objects}
            terminals={props.terminals}
            facts={props.facts}
            places={props.places}
            colorMode="device"
            deviceColors={props.deviceColors}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="疑似同一实体 · 未合并" icon="ri-question-line" count={summary.duplicateCandidates.length}>
          <p className="text-[10px] text-foreground-400 mb-2 leading-relaxed">
            同名、出现在多台设备上 —— 但实体统一 ID 尚未建立,这里只列候选,任何计数都不把它们归一。
          </p>
          {summary.duplicateCandidates.length === 0 ? <Empty hint="暂无跨设备同名实体" /> : (
            <ul className="space-y-1.5">
              {summary.duplicateCandidates.map((d, i) => (
                <li key={i} className="flex items-center gap-2 text-[11px]">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${d.kind === 'place' ? 'bg-orange-50 text-orange-600' : 'bg-amber-50 text-amber-600'}`}>
                    {d.kind === 'place' ? '地点' : '物体'}
                  </span>
                  <span className="text-foreground-800 font-medium">{d.name}</span>
                  <span className="text-foreground-400">{d.nDevices} 台设备:{d.devices.map(nameOf).join('、')}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="各设备同步状态" icon="ri-refresh-line" count={summary.devices.length + summary.neverPushed.length}>
          <table className="w-full text-[11px]">
            <thead><tr className="text-foreground-400 text-left">
              <th className="pb-1.5 font-medium">设备</th><th className="pb-1.5 font-medium">知识量</th>
              <th className="pb-1.5 font-medium">拦截涉人行</th><th className="pb-1.5 font-medium">最近上报</th>
            </tr></thead>
            <tbody>
              {summary.devices.map((d) => (
                <tr key={d.deviceId} className="border-t border-background-100">
                  <td className="py-1.5 text-foreground-800">{d.name || d.deviceId}</td>
                  <td className="py-1.5 font-mono text-foreground-500">{d.nPlaces}地 / {d.nObjects}物 / {d.nFacts}事</td>
                  <td className="py-1.5 text-emerald-600">{d.dropped ? d.dropped.places + d.dropped.attrs + d.dropped.facts : 0}</td>
                  <td className="py-1.5 text-foreground-400">{syncTime(d.syncedAt)}</td>
                </tr>
              ))}
              {summary.neverPushed.map((n) => (
                <tr key={n} className="border-t border-background-100">
                  <td className="py-1.5 text-foreground-800">{n}</td>
                  <td className="py-1.5 text-foreground-400" colSpan={3}>待首次上报(设备尚未推送知识快照)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}

/* ─── 小件 ────────────────────────────────────────────────────────────── */
function Panel({ title, icon, count, children }: { title: string; icon: string; count: number; children: React.ReactNode }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
      <h3 className="flex items-center gap-2 text-xs font-bold text-foreground-800 mb-3">
        <i className={`${icon} text-sm text-foreground-400`} />{title}
        <span className="text-[10px] font-mono text-foreground-400">{count}</span>
      </h3>
      <div className="max-h-72 overflow-y-auto">{children}</div>
    </div>
  );
}

function Empty({ hint }: { hint: string }) {
  return <p className="text-[11px] text-foreground-400 py-4 text-center">{hint}</p>;
}

function Stat({ icon, label, value, note }: { icon: string; label: string; value: string; note: string }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-3.5">
      <div className="flex items-center gap-2 text-[11px] text-foreground-400"><i className={`${icon} text-sm`} />{label}</div>
      <div className="text-xl font-bold text-foreground-900 font-heading mt-1">{value}</div>
      <div className="text-[10px] text-foreground-400 mt-0.5">{note}</div>
    </div>
  );
}

/* ─── 页面 ────────────────────────────────────────────────────────────── */
export default function KnowledgePage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'group' ? 'group' : 'unit';
  const [selected, setSelected] = useState('');
  const { terminals } = useTerminals();
  // ★ 单体视图必须落到【一台具体设备】才取数 —— selected 为空时绝不退化成并集,
  //   否则 meta 条(单设备)和图谱(全设备)会各说各话。第一台设备从 summary 里拿。
  const wantDevice = view === 'group' ? undefined : (selected || undefined);
  const { graph, summary, loading, error, unavailable, refetch } = useKnowledge(wantDevice);

  useEffect(() => {
    if (view === 'unit' && !selected && summary && summary.devices.length > 0) {
      setSelected(summary.devices[0].deviceId);
    }
  }, [view, selected, summary]);

  const deviceName = useMemo(() => {
    const m = new Map<string, string>();
    terminals.forEach((t) => m.set(t.id, t.name));
    return m;
  }, [terminals]);

  const effectiveDevice = selected || summary?.devices[0]?.deviceId || terminals[0]?.id || '';

  const setView = (v: 'unit' | 'group') => setParams(v === 'unit' ? {} : { view: v }, { replace: true });

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 animate-fade-in-up">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-foreground-900 font-heading">认知管理 · 知识图谱</h1>
            <p className="text-xs text-foreground-500 mt-0.5">地点 · 物体 · 事实 —— PIPL 过滤后的知识;人物与对话永不出设备</p>
          </div>
          <div className="flex rounded-lg border border-background-200/70 overflow-hidden">
            <button onClick={() => setView('unit')}
              className={`px-4 py-1.5 text-xs font-medium transition-colors cursor-pointer ${view === 'unit' ? 'bg-primary-600 text-white' : 'bg-background-50 text-foreground-500 hover:bg-background-100'}`}>
              <i className="ri-robot-line mr-1" />单体
            </button>
            <button onClick={() => setView('group')}
              className={`px-4 py-1.5 text-xs font-medium transition-colors cursor-pointer ${view === 'group' ? 'bg-primary-600 text-white' : 'bg-background-50 text-foreground-500 hover:bg-background-100'}`}>
              <i className="ri-group-line mr-1" />群体
            </button>
          </div>
        </div>

        {view === 'unit' && terminals.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {terminals.map((t) => (
              <button key={t.id} onClick={() => setSelected(t.id)}
                className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors cursor-pointer ${effectiveDevice === t.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                  : 'border-background-200/70 bg-background-50 text-foreground-500 hover:bg-background-100'}`}>
                <i className="ri-robot-line mr-1" />{t.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
        {loading ? <Spinner />
          : unavailable ? (
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <NoDataSource title="知识库未配置" reason={unavailable} phase="hub 需要 knowledge.db(服务端升级后自动具备)" />
            </div>
          ) : error ? <LoadError error={error} onRetry={refetch} />
            : !graph || !summary ? <Spinner />
              : view === 'group' ? <GroupView graph={graph} summary={summary} />
                // 单体未落到具体设备前等 effect,绝不拿并集顶替单体
                : !selected ? <Spinner />
                : summary.devices.length === 0 && summary.neverPushed.some((n) => n.includes(effectiveDevice)) ? (
                  <div className="bg-background-50 border border-background-200/70 rounded-lg">
                    <NoDataSource title="等待设备首次上报"
                      reason={`设备 ${deviceName.get(effectiveDevice) || effectiveDevice} 已注册,但还没推送过知识快照。设备端每 5 分钟推一次;若长期为空,检查该设备的 OXMAN_DEVICE_TOKEN。`} />
                  </div>
                ) : (
                  <UnitView deviceId={effectiveDevice} graph={graph} summary={summary} />
                )}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 animate-fade-in-up" style={{ animationDelay: '0.16s' }}>
        {[
          { icon: 'ri-shield-keyhole-line', title: '人物不出设备', body: '人脸 embedding、姓名、对话记忆按 PIPL 永不进云端 —— 本页所有数据都经过了设备侧人名过滤。' },
          { icon: 'ri-filter-3-line', title: '过滤可见', body: '每条"拦截涉人行"计数都是设备真实拦下的:地点名/描述、物体属性、事实及其源对话提及人名即整条丢弃。' },
          { icon: 'ri-route-line', title: '群体不合并', body: '跨设备同名实体只列"疑似候选"。实体统一 ID 建立之前,任何合计数都不做归一。' },
        ].map((c) => (
          <div key={c.title} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 mb-2"><i className={`${c.icon} text-base`} /></span>
            <h3 className="text-xs font-bold text-foreground-800 mb-1">{c.title}</h3>
            <p className="text-[11px] text-foreground-500 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
